import { TutorialsBadgeIcon } from "@xola/icons";
import { Tooltip } from "@xola/ui-kit";
import { minimatch } from "minimatch";
import React, { useCallback } from "react";
import { Permission } from "types";
import { useTutorialCollection } from "../../api/tutorialsApi";
import { config } from "../../config";
import { useAuthorization } from "../../hooks/useAuthorization";
import { useModal } from "../../hooks/useVisibility";
import { Tutorial, TutorialStatus } from "../../types/Tutorial";
import { TutorialModal } from "./TutorialModal";

export const useTutorialBadge = () => {
    const { isGranted } = useAuthorization();
    const { href, pathname, search } = window.location;

    const hasTutorials = isGranted(Permission.TUTORIALS) && !!config.tutorials.url;
    const { data: tutorials = [] } = useTutorialCollection<Tutorial>("tutorials", null, { enabled: hasTutorials });

    // Find if there is an exact match on the URL as that takes precedent
    const tutorialExactMatch = tutorials.find((tutorial) => {
        const urls = tutorial.urls ?? [];
        return urls.find((url) => url === pathname || url === href);
    });

    const tutorialGlobMatch = tutorials.find((tutorial) => {
        const list = tutorial.urls?.filter((urlString) => {
            if (urlString) {
                const url = new URL(urlString);
                const source = `${pathname}${search}`;
                const destination = `${url.pathname}${url.search}`;

                return minimatch(source, destination);
            }

            return null;
        });

        return list ? list.length > 0 : false;
    });

    return tutorialExactMatch ?? tutorialGlobMatch ?? null;
};

export const InjectTutorialBadge = React.memo(({ tutorial }: { tutorial: Tutorial }) => {
    const tutorialModal = useModal(false);

    const handleTutorialClick = useCallback(() => {
        if (tutorial) {
            tutorialModal.toggle();
        } else {
            console.log("%cNo tutorial, weird", "color:red");
        }
    }, [tutorialModal, tutorial]);

    const isDraft = tutorial.status === TutorialStatus.DRAFT;
    const name = `${tutorial.name} ${isDraft ? "(Draft)" : ""}`;

    return (
        <div
            className="tutorial no-translate fixed bottom-0 right-20 z-10 cursor-pointer"
            onClick={handleTutorialClick}
        >
            <Tooltip content={name} offset={[0, -15]}>
                <TutorialsBadgeIcon />
                {isDraft && (
                    <span className="relative left-[48%] bottom-12 mt-1 inline-block text-xs font-bold text-white">
                        Draft
                    </span>
                )}
            </Tooltip>
            <TutorialModal tutorial={tutorial} isOpen={tutorialModal.isVisible} onClose={tutorialModal.toggle} />
        </div>
    );
});
